import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Building2, ChevronRight, IndianRupee, Inbox, Plus, Sparkles, Users } from 'lucide-react';
import { usePlatformOverview, type PlatformDays } from '@/api/platform';
import type { PlatformOverview } from '@/api/types';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Segmented,
  Skeleton,
} from '@/components/ui';
import { ColumnChart, Legend, LineChart, SERIES } from '@/features/analytics/charts';
import { Kpi } from '@/features/analytics/Kpi';
import { cn } from '@/lib/cn';
import { formatMoney, formatNumber, formatTokens, humanize } from '@/lib/format';
import { AiProvidersTable, AiUsageBreakdown } from './AiPanels';
import { ClientFormModal } from './ClientFormModal';
import { PLAN_TONE, STATUS_TONE, failureRate, monthLabel, providerLabel } from './meta';

const RANGES: PlatformDays[] = [7, 30, 90];

function Section({
  title,
  subtitle,
  children,
  className,
  actions,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}) {
  return (
    <Card className={cn('p-5', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </Card>
  );
}

/** Revenue per plan: client count × list price, with each plan's share of MRR. */
function PlanBreakdown({
  plans,
  currency,
  mrr,
}: Pick<PlatformOverview, 'plans' | 'currency'> & { mrr: number }) {
  const max = Math.max(1, ...plans.map((p) => p.mrr));
  return (
    <ul className="space-y-4">
      {plans.map((p) => (
        <li key={p.plan}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2">
              <Badge tone={PLAN_TONE[p.plan]}>{humanize(p.plan)}</Badge>
              <span className="text-xs text-slate-500">
                {p.clients} {p.clients === 1 ? 'client' : 'clients'} ×{' '}
                {p.price ? formatMoney(p.price, currency) : 'free'}
              </span>
            </span>
            <span className="text-xs text-slate-500 tabular-nums">
              <span className="font-semibold text-slate-900">{formatMoney(p.mrr, currency)}</span>
              {mrr > 0 && ` · ${Math.round((p.mrr / mrr) * 100)}%`}
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100">
            {p.mrr > 0 && (
              <div
                className="h-2 rounded-full"
                style={{ width: `${Math.max(2, (p.mrr / max) * 100)}%`, background: SERIES[0] }}
              />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ClientsTable({ o }: { o: PlatformOverview }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[880px] text-left text-sm">
        <thead className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
          <tr className="border-b border-slate-100">
            <th scope="col" className="px-4 py-3">
              Client
            </th>
            <th scope="col" className="px-4 py-3">
              Plan
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              MRR
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              Team
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              Tickets ({o.range.days}d)
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              Open
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              AI requests
            </th>
            <th scope="col" className="px-4 py-3">
              AI providers
            </th>
            <th scope="col" className="px-2 py-3">
              <span className="sr-only">Details</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {o.clients.map((c) => (
            <tr key={c.id} className="group hover:bg-slate-50">
              <td className="px-4 py-3">
                <Link
                  to={`/admin/clients/${c.id}`}
                  className="hover:text-brand-700 font-medium text-slate-900"
                >
                  {c.name}
                </Link>
                <p className="font-mono text-xs text-slate-500">{c.slug}</p>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  <Badge tone={PLAN_TONE[c.plan]}>{humanize(c.plan)}</Badge>
                  {c.status !== 'active' && <Badge tone={STATUS_TONE[c.status]}>{humanize(c.status)}</Badge>}
                </div>
              </td>
              <td className="px-4 py-3 text-right font-medium text-slate-900 tabular-nums">
                {c.mrr ? formatMoney(c.mrr, o.currency) : '—'}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{c.counts.users}</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatNumber(c.counts.ticketsCreated)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatNumber(c.counts.openTickets)}</td>
              <td className="px-4 py-3 text-right tabular-nums">
                {formatNumber(c.counts.aiRequests)}
                {c.counts.aiInputTokens + c.counts.aiOutputTokens > 0 && (
                  <span className="block text-[11px] text-slate-500">
                    {formatTokens(c.counts.aiInputTokens + c.counts.aiOutputTokens)} tokens
                  </span>
                )}
                {c.counts.aiFailed > 0 && (
                  <span className="block text-[11px] text-slate-500">{c.counts.aiFailed} failed</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex max-w-56 flex-wrap gap-1">
                  {c.aiProviders.length ? (
                    c.aiProviders.map((p) => <Badge key={p}>{providerLabel(p)}</Badge>)
                  ) : (
                    <span className="text-xs text-slate-400">Not connected</span>
                  )}
                  {c.aiAgents > 0 && (
                    <Badge tone="brand">
                      {c.aiAgents} AI {c.aiAgents === 1 ? 'agent' : 'agents'}
                    </Badge>
                  )}
                </div>
              </td>
              <td className="px-2 py-3">
                <Link
                  to={`/admin/clients/${c.id}`}
                  aria-label={`${c.name} details`}
                  className="text-slate-400 group-hover:text-slate-700"
                >
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Super admin home: revenue by plan, platform-wide ticket volume and AI usage across clients. */
export function PlatformOverviewPage() {
  const [days, setDays] = useState<PlatformDays>(30);
  const [creating, setCreating] = useState(false);
  const overview = usePlatformOverview(days);
  const rangePicker = (
    <Segmented
      label="Date range"
      value={days}
      onChange={setDays}
      options={RANGES.map((d) => ({ value: d, label: `${d} days` }))}
    />
  );
  const header = (description?: string) => (
    <PageHeader eyebrow="Platform" title="Overview" description={description} actions={rangePicker} />
  );

  if (overview.isPending) {
    return (
      <div className="mx-auto max-w-7xl" role="status" aria-label="Loading platform overview">
        {header()}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="mt-5 h-80 rounded-2xl" />
      </div>
    );
  }
  if (overview.error) {
    return (
      <div className="mx-auto max-w-7xl">
        {header()}
        <Card>
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        </Card>
      </div>
    );
  }

  const o = overview.data;
  const t = o.totals;

  // A fresh platform: nothing to chart until the first client exists.
  if (t.clients === 0) {
    return (
      <div className="mx-auto max-w-7xl">
        <PageHeader eyebrow="Platform" title="Overview" />
        <Card>
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title="No clients yet"
            description="Create your first client to give them a workspace. Revenue, ticket volume and AI usage appear here as clients start working."
            action={
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" /> New client
              </Button>
            }
          />
        </Card>
        {creating && <ClientFormModal onClose={() => setCreating(false)} />}
      </div>
    );
  }

  const aiFail = failureRate(t.aiRequests, t.aiFailed);
  const money = (v: number) => formatMoney(v, o.currency);

  return (
    <div className={cn('mx-auto max-w-7xl transition-opacity', overview.isPlaceholderData && 'opacity-60')}>
      {header(`All clients, last ${days} days (IST). Revenue is estimated from plan list prices.`)}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi
          label="Est. monthly revenue"
          value={formatMoney(t.mrr, o.currency, true)}
          hint="Active paying clients × their plan's monthly list price"
          icon={IndianRupee}
          tint="bg-emerald-50 text-emerald-600 ring-emerald-100"
          trend={{ values: o.revenueTrend.map((m) => m.mrr), color: SERIES[0] }}
        >
          <p className="text-xs text-slate-500">ARR {formatMoney(t.arr, o.currency, true)}</p>
        </Kpi>
        <Kpi
          label="Active clients"
          value={formatNumber(t.activeClients)}
          icon={Building2}
          tint="bg-brand-50 text-brand-600 ring-brand-100"
        >
          <p className="text-xs text-slate-500">
            {t.payingClients} paying · {t.trialClients} trial
            {t.suspendedClients > 0 && ` · ${t.suspendedClients} suspended`}
          </p>
        </Kpi>
        <Kpi
          label={`New clients (${days}d)`}
          value={formatNumber(t.newClients)}
          icon={Sparkles}
          tint="bg-accent-50 text-accent-500 ring-accent-100"
        >
          <p className="text-xs text-slate-500">{t.clients} total</p>
        </Kpi>
        <Kpi
          label={`Tickets (${days}d)`}
          value={formatNumber(t.ticketsCreated)}
          icon={Inbox}
          tint="bg-sky-50 text-sky-600 ring-sky-100"
          trend={{ values: o.tickets.map((d) => d.created), color: SERIES[0] }}
        >
          <p className="text-xs text-slate-500">
            {formatNumber(t.ticketsSolved)} solved · {formatNumber(t.openTickets)} open now
          </p>
        </Kpi>
        <Kpi
          label={`AI requests (${days}d)`}
          value={formatNumber(t.aiRequests)}
          icon={Bot}
          tint="bg-indigo-50 text-indigo-600 ring-indigo-100"
          trend={{ values: o.ai.daily.map((d) => d.ok + d.failed), color: SERIES[0] }}
        >
          <p className="text-xs text-slate-500">
            {aiFail == null ? 'No requests yet' : `${aiFail}% failed`} ·{' '}
            {formatTokens(t.aiInputTokens + t.aiOutputTokens)} tokens · {t.aiEnabledClients} clients connected
          </p>
        </Kpi>
        <Kpi
          label="Team members"
          value={formatNumber(t.users)}
          icon={Users}
          tint="bg-teal-50 text-teal-600 ring-teal-100"
        >
          <p className="text-xs text-slate-500">{formatNumber(t.customers)} end customers</p>
        </Kpi>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <Section
          className="xl:col-span-2"
          title="Monthly recurring revenue"
          subtitle="Estimated: each client at its current plan's list price from the month it joined, until suspended."
        >
          <ColumnChart
            title="Estimated MRR per month"
            formatValue={(v) => formatMoney(v, o.currency, true)}
            bars={o.revenueTrend.map((m) => ({
              label: monthLabel(m.month),
              value: m.mrr,
              detail: `${m.payingClients} paying ${m.payingClients === 1 ? 'client' : 'clients'}`,
            }))}
          />
        </Section>
        <Section title="Revenue by plan" subtitle={`Current MRR ${money(t.mrr)}`}>
          <PlanBreakdown plans={o.plans} currency={o.currency} mrr={t.mrr} />
        </Section>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <Section
          title="Ticket volume, all clients"
          subtitle="Created and solved per day"
          actions={<Legend labels={['Created', 'Solved']} />}
        >
          <LineChart
            dates={o.tickets.map((d) => d.date)}
            series={[
              { label: 'Created', values: o.tickets.map((d) => d.created) },
              { label: 'Solved', values: o.tickets.map((d) => d.solved) },
            ]}
          />
        </Section>
        <Section title="AI requests per day" subtitle="Every AI completion across clients, successful or not">
          <LineChart
            dates={o.ai.daily.map((d) => d.date)}
            series={[{ label: 'AI requests', values: o.ai.daily.map((d) => d.ok + d.failed) }]}
          />
        </Section>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <Section
          className="xl:col-span-2"
          title="AI services in use"
          subtitle="Requests by provider in this period, and how many clients rely on each"
        >
          <AiProvidersTable rows={o.ai.byProvider} />
          {o.ai.connectionsByProvider.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                Connected providers
              </p>
              <div className="flex flex-wrap gap-2">
                {o.ai.connectionsByProvider.map((c) => (
                  <span
                    key={c.provider}
                    className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-700"
                  >
                    <span className="font-medium text-slate-900">{providerLabel(c.provider)}</span> ·{' '}
                    {c.clients} {c.clients === 1 ? 'client' : 'clients'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Section>
        <Section title="AI usage">
          <AiUsageBreakdown ai={o.ai} />
        </Section>
      </div>

      <Card className="mt-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="font-heading text-base font-bold text-slate-900">Clients</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Sorted by revenue. Open one for its full breakdown.
            </p>
          </div>
          <Link to="/admin/clients" className="text-brand-700 text-sm font-medium hover:underline">
            Manage clients
          </Link>
        </div>
        <ClientsTable o={o} />
      </Card>
    </div>
  );
}
